import { addEntity, addComponent } from 'bitecs';
import { Position, Structure, Production, ANIMAL_TYPES, AI, AI_STATE } from '../world.js';
import { Sprite as PixiSprite, Graphics, Text, Container, SCALE_MODES, Assets, Texture } from 'pixi.js';
import { createBee } from './bee.js';
import { TILE_SIZE } from '../config.js';

let app = null;

export function setApp(pixiApp) {
    console.log('[HIVE] Setting PIXI app:', pixiApp ? 'valid object' : 'null/undefined');
    if (!pixiApp || !pixiApp.renderer) {
        console.error('[HIVE] Invalid PIXI app provided');
        return;
    }
    app = pixiApp;
    console.log('[HIVE] PIXI app set successfully with renderer:', app.renderer ? 'available' : 'missing');

    // Debug check for worldContainer
    if (app.worldContainer) {
        console.log('[HIVE] worldContainer is available in app');
    } else {
        console.warn('[HIVE] worldContainer is missing from app - hives may not render properly');
    }
}

export function createHive(world, x, y) {
    console.log('[HIVE] Creating hive at position:', { x, y });
    
    const hive = addEntity(world);
    console.log('[HIVE] Created hive entity with ID:', hive);
    
    // Add components
    Position.x[hive] = x;
    Position.y[hive] = y;
    console.log('[HIVE] Set position:', { x: Position.x[hive], y: Position.y[hive] });
    
    addComponent(world, Structure, hive);
    console.log('[HIVE] Added Structure component to entity #', hive);
    
    Structure.type[hive] = 1; // 1 for hive
    Structure.health[hive] = 100;
    Structure.maxHealth[hive] = 100;
    
    // Verify component was added correctly
    console.log('[HIVE] Structure component verification:', {
        hasStructureComponent: Structure.type && Structure.type[hive] !== undefined,
        typeValue: Structure.type[hive],
        health: Structure.health[hive]
    });
    
    // Fix: Add Production component properly
    addComponent(world, Production, hive);
    console.log('[HIVE] Added Production component');
    
    Production.type[hive] = 1; // 1 for honey
    Production.amount[hive] = 0;
    Production.rate[hive] = 0.1;
    Production.maxAmount[hive] = 100;
    console.log('[HIVE] Production component values:', {
        type: Production.type[hive],
        amount: Production.amount[hive],
        rate: Production.rate[hive],
        maxAmount: Production.maxAmount[hive]
    });
    
    // Add to world entities array if it exists
    if (world.entities) {
        world.entities.push(hive);
    }
    
    // Create hive sprite
    let sprite;
    try {
        console.log('[HIVE] Creating hive sprite');
        
        // Determine the tile position based on the world coordinates
        const tileX = Math.floor(x / TILE_SIZE);
        const tileY = Math.floor(y / TILE_SIZE);
        
        // Get the center of the tile in world coordinates
        const tileCenterX = (tileX * TILE_SIZE) + (TILE_SIZE / 2);
        const tileCenterY = (tileY * TILE_SIZE) + (TILE_SIZE / 2);
        
        console.log(`[HIVE] Calculated tile position: Tile(${tileX}, ${tileY}), Center(${tileCenterX}, ${tileCenterY})`);
        
        // Create container for the hive graphics
        const container = new Container();
        
        // Size of the hive (percentage of tile size)
        const hiveSize = TILE_SIZE * 0.7;
        
        // Try to load the hive texture
        let hiveTexture;
        if (app && app.renderer) {
            // Try to get the texture from Assets cache first
            hiveTexture = Assets.get('assets/images/structures/hive');
            if (!hiveTexture || hiveTexture === Texture.WHITE) {
                console.warn('[HIVE] Hive texture not found in Assets cache, trying to load directly');
                // Try to load the texture directly
                hiveTexture = Texture.from('assets/images/structures/hive.png');
            }
        } else {
            console.warn('[HIVE] app.renderer not available - using fallback texture');
            hiveTexture = Texture.WHITE;
        }
        
        // Create the sprite with the hive texture
        sprite = new PixiSprite(hiveTexture);
        sprite.anchor.set(0.5);
        
        // Set the size based on tile size
        sprite.width = hiveSize;
        sprite.height = hiveSize;
        
        // Position at the exact center of the tile
        sprite.position.set(tileCenterX, tileCenterY);
        
        // Update entity position to match tile center
        Position.x[hive] = tileCenterX;
        Position.y[hive] = tileCenterY;
        
        sprite.alpha = 1;
        sprite.visible = true;
        sprite.zIndex = Math.floor(tileCenterY / TILE_SIZE);
        
        // Store data about the hive's honey production
        sprite.honeyAmount = 0;
        sprite.maxHoneyAmount = Production.maxAmount[hive];
        
        // Clean up container
        container.destroy();
        
        // Add sprite to world container if available
        let worldContainer = null;
        if (app && app.worldContainer) {
            worldContainer = app.worldContainer;
        } else if (world.worldContainer) {
            worldContainer = world.worldContainer;
        } else if (app && app.stage) {
            // Try to find world container in stage
            const possibleWorldContainer = app.stage.children.find(c => c.name === 'worldContainer');
            if (possibleWorldContainer) {
                worldContainer = possibleWorldContainer;
                if (app) app.worldContainer = possibleWorldContainer; // Cache for future use
            }
        }
        
        if (worldContainer) {
            console.log('[HIVE] Found world container:', {
                name: worldContainer.name || 'unnamed',
                childCount: worldContainer.children.length,
                zIndex: worldContainer.zIndex
            });
            
            // Remove any existing sprite for this entity
            if (world.sprites && world.sprites[hive]) {
                const oldSprite = world.sprites[hive];
                if (oldSprite.parent) {
                    oldSprite.parent.removeChild(oldSprite);
                }
                oldSprite.destroy();
            }
            
            worldContainer.addChild(sprite);
            
            // Verify sprite was added to world container
            const spriteIndex = worldContainer.children.indexOf(sprite);
            console.log(`[HIVE] Sprite added to world container at index ${spriteIndex}`);
            console.log(`[HIVE] WorldContainer now has ${worldContainer.children.length} children`);
            
            // Force update of children to ensure sprite is included
            worldContainer.sortChildren();
            console.log('[HIVE] Added sprite to world container');
        } else {
            console.error('[HIVE] Could not find world container - sprite will not be rendered');
            // Don't fail completely - we'll still create the entity and its components
        }
        
        console.log('[HIVE] Created sprite:', { 
            width: sprite.width,
            height: sprite.height,
            zIndex: sprite.zIndex,
            position: `(${sprite.x}, ${sprite.y})`,
            visible: sprite.visible,
            alpha: sprite.alpha,
            hasParent: !!sprite.parent,
            parentName: sprite.parent ? (sprite.parent.name || 'unnamed') : 'none'
        });
    } catch (error) {
        console.error('[HIVE] Error creating hive sprite:', error);
        // Don't fail completely - create a simple fallback sprite
        try {
            console.log('[HIVE] Creating fallback sprite');
            sprite = new PixiSprite(Texture.WHITE);
            sprite.width = TILE_SIZE * 0.8;
            sprite.height = TILE_SIZE * 0.8;
            sprite.tint = 0xD2B48C; // Tan color for hive
            sprite.position.set(x, y);
            sprite.anchor.set(0.5);
            
            // Try to add to world container if possible
            if (app && app.worldContainer) {
                app.worldContainer.addChild(sprite);
            }
        } catch (fallbackError) {
            console.error('[HIVE] Failed to create fallback sprite:', fallbackError);
            // Just return the entity without a sprite
        }
    }
    
    // Store sprite reference if we created one
    if (sprite) {
        if (!world.sprites) world.sprites = {};
        world.sprites[hive] = sprite;
        console.log('[HIVE] Stored sprite reference in world.sprites');
        
        // Force a redraw of the sprite
        sprite.visible = false;
        setTimeout(() => {
            sprite.visible = true;
            if (sprite.parent) {
                sprite.parent.sortChildren();
            }
        }, 0);
    } else {
        console.warn('[HIVE] No sprite created for hive');
    }
    
    // Spawn initial bees for the hive
    spawnInitialBeesForHive(world, hive);
    
    return hive;
}

// Helper function to spawn initial bees for a hive
function spawnInitialBeesForHive(world, hive, count = 3) {
    const hiveX = Position.x[hive];
    const hiveY = Position.y[hive];
    
    console.log(`[HIVE] Spawning ${count} initial bees for hive #${hive} at (${hiveX}, ${hiveY})`);
    
    const createdBees = [];
    for (let i = 0; i < count; i++) {
        // Distribute bees around the hive in a circle
        const angle = (i / count) * Math.PI * 2;
        const distance = 32; // 32 units away from hive
        const beeX = hiveX + Math.cos(angle) * distance;
        const beeY = hiveY + Math.sin(angle) * distance;
        
        // Pass Position component explicitly
        const bee = createBee(world, beeX, beeY, hive, Position);
        if (bee) {
            createdBees.push(bee);
            console.log(`[HIVE] Spawned bee #${bee} at (${beeX.toFixed(1)}, ${beeY.toFixed(1)})`);
            
            // Initialize AI state to MOVING
            if (AI && AI.state) {
                AI.state[bee] = AI_STATE.MOVING;
            }
        }
    }
    
    console.log(`[HIVE] Created ${createdBees.length} bees for hive #${hive}`);
    return createdBees;
}

// Add a debug helper to check hive entity and sprite
export function debugHiveStructure(world, hiveId) {
    console.log('=============== DEBUG HIVE STRUCTURE ===============');
    
    if (!hiveId) {
        console.log('[DEBUG] No hiveId provided, looking for hive entities...');
        // Try to find a structure with type 1 (hive)
        for (let eid = 1; eid < 10000; eid++) {
            if (Structure.type && Structure.type[eid] === 1) {
                hiveId = eid;
                console.log(`[DEBUG] Found potential hive entity with ID: ${hiveId}`);
                break;
            }
        }
    }
    
    if (!hiveId) {
        console.error('[DEBUG] Could not find any hive entity');
        return;
    }
    
    // Basic entity verification
    console.log(`[DEBUG] Checking hive entity ${hiveId}:`);
    
    // Check Structure component
    if (Structure.type) {
        console.log(`[DEBUG] Structure.type[${hiveId}] = ${Structure.type[hiveId]}`);
    } else {
        console.error('[DEBUG] Structure.type is not defined');
    }
    
    // Check Position component
    if (Position.x && Position.y) {
        console.log(`[DEBUG] Position: (${Position.x[hiveId]}, ${Position.y[hiveId]})`);
    } else {
        console.error('[DEBUG] Position components not defined');
    }
    
    // Check for sprite in world
    if (world.sprites && world.sprites[hiveId]) {
        const sprite = world.sprites[hiveId];
        console.log('[DEBUG] Sprite found in world.sprites:', {
            width: sprite.width,
            height: sprite.height,
            visible: sprite.visible,
            alpha: sprite.alpha,
            position: `(${sprite.x}, ${sprite.y})`,
            zIndex: sprite.zIndex,
            hasParent: !!sprite.parent,
            parentName: sprite.parent ? (sprite.parent.name || 'unnamed') : 'none'
        });
        
        // Check if parent container is visible
        if (sprite.parent) {
            console.log('[DEBUG] Parent container:', {
                visible: sprite.parent.visible,
                alpha: sprite.parent.alpha,
                position: `(${sprite.parent.x}, ${sprite.parent.y})`,
                scale: `(${sprite.parent.scale.x}, ${sprite.parent.scale.y})`,
                childCount: sprite.parent.children.length
            });
            
            // Check if sprite is in parent's children
            const index = sprite.parent.children.indexOf(sprite);
            console.log(`[DEBUG] Sprite is child #${index} in parent container`);
        }
    } else {
        console.error('[DEBUG] No sprite found in world.sprites for entity');
    }
    
    console.log('=============== END DEBUG ===============');
} 